/**
 * Trade Area Map — the territory, and what is inside the one you clicked.
 *
 * The map is the index; the panels underneath are the report. Clicking any
 * boundary selects the **trade area** it belongs to, not that metro, because
 * the area is the unit the business works in: a distributor in Cleveland
 * serves Akron, Canton and Youngstown, and treating those as four territories
 * would show one worth having and three empty.
 *
 * What opens underneath, in the order a territory review asks for it:
 *
 *   1. the metros and micros inside the area, with their distance from the hub
 *   2. the distributors serving it, and when each last ordered
 *   3. the lines we sell there, each against the region's own mix
 *   4. the lines the region sells that this area does not
 *   5. open agent findings that land in it
 *
 * Nothing is computed here. Every figure, including the value the map shades
 * by, arrives in the payload where `derive` owns it.
 */

import { lazy, Suspense, useMemo, useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import type { MapArea } from './CbsaMap'

// Lazy for the same reason `VisualHost` loads `Chart` lazily: ECharts is the
// largest thing in the bundle, and a reader who never opens this report should
// not download a map renderer to read a table.
const CbsaMap = lazy(() => import('./CbsaMap'))

const NONE = '—'

const MEASURE_LABELS: Readonly<Record<string, string>> = {
  gallons_ttm: 'Gallons TTM',
  fob_ttm: 'FOB TTM',
}

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function whole(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

function money(value: number | null): string {
  return value === null ? NONE : `$${Math.round(value).toLocaleString('en-US')}`
}

function pct(value: number | null, places = 1): string {
  return value === null ? NONE : `${value.toFixed(places)}%`
}

function signedPct(value: number | null): string {
  if (value === null) return NONE
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}pp`
}

export function TradeAreaMap({ result, values }: CustomReportProps) {
  const areas = dataset(result, 'areas')
  const members = dataset(result, 'members')
  const distributors = dataset(result, 'area_distributors')
  const products = dataset(result, 'area_products')
  const branchProducts = dataset(result, 'distributor_products')
  const absentLines = dataset(result, 'area_absent_lines')
  const findings = dataset(result, 'area_findings')
  const consolidators = dataset(result, 'area_consolidators')
  const regionTotals = dataset(result, 'region_totals')
  const asOf = dataset(result, 'as_of')

  const measureKey = typeof values['measure'] === 'string' ? values['measure'] : 'gallons_ttm'
  const measureLabel = MEASURE_LABELS[measureKey] ?? 'Value'

  const [selected, setSelected] = useState<number | null>(null)
  // Which distributor inside the selected area the product panel is showing.
  // null means the whole area, which is what the panel falls back to whenever
  // the area changes -- a branch from the previous area would otherwise stay
  // selected and the panel would show a mix from somewhere else.
  const [branch, setBranch] = useState<string | null>(null)

  const mapAreas: MapArea[] = useMemo(
    () =>
      members
        .map((row) => ({
          cbsaCode: String(count(row['cbsa_code'])),
          cbsaTitle: text(row['cbsa_title']) ?? NONE,
          cbsaClass: text(row['cbsa_class']) ?? NONE,
          tradeAreaCode: count(row['trade_area_code']),
          tradeAreaTitle: text(row['trade_area_title']) ?? NONE,
          isHub: text(row['is_hub']) === 'true',
          coverage: text(row['coverage']) ?? NONE,
          shaded: text(row['shaded']) === 'true',
          value: num(row['measure_value']),
        }))
        .filter((a) => a.cbsaCode !== '0'),
    [members],
  )

  const forArea = (rows: Keyed[]): Keyed[] =>
    selected === null ? [] : rows.filter((row) => count(row['trade_area_code']) === selected)

  const area = areas.find((row) => count(row['trade_area_code']) === selected)
  const areaMembers = forArea(members)
  const areaDistributors = forArea(distributors)
  const areaProducts = forArea(products)
  const areaBranchProducts = forArea(branchProducts)
  const shownBranch =
    branch !== null && areaDistributors.some((row) => text(row['ship_to_number']) === branch)
      ? branch
      : null
  const branchMix = areaBranchProducts.filter(
    (row) => text(row['ship_to_number']) === shownBranch,
  )
  const branchRow = areaDistributors.find((row) => text(row['ship_to_number']) === shownBranch)
  const areaAbsent = forArea(absentLines)
  const areaFindings = forArea(findings)
  const areaConsolidators =
    area === undefined
      ? []
      : consolidators.filter(
          (row) => text(row['trade_area_title']) === text(area['trade_area_title']),
        )

  const totals = useMemo(
    () => ({
      areas: areas.length,
      restaurants: areas.reduce((sum, r) => sum + count(r['food_service_estab']), 0),
      population: areas.reduce((sum, r) => sum + count(r['population']), 0),
      gallons: areas.reduce((sum, r) => sum + (num(r['gallons_ttm']) ?? 0), 0),
      headroom: areas.reduce((sum, r) => sum + (num(r['headroom_gal']) ?? 0), 0),
      dollars: areas.reduce((sum, r) => sum + (num(r['headroom_dollars']) ?? 0), 0),
    }),
    [areas],
  )

  return (
    <div className="bespoke-report tam-page">
      <section className="tam-kpis">
        <Kpi label="Trade areas" value={totals.areas.toLocaleString('en-US')} />
        <Kpi label="Restaurants covered" value={totals.restaurants.toLocaleString('en-US')} />
        <Kpi label="Population covered" value={totals.population.toLocaleString('en-US')} />
        <Kpi label="Gallons TTM" value={whole(totals.gallons)} />
        <Kpi label="Headroom to region median" value={`${whole(totals.headroom)} gal`} />
        <Kpi label="At list price" value={money(totals.dollars)} />
      </section>

      <Card
        title="Where we sell — metro and micro areas"
        subtitle={
          'Real CBSA boundaries, contiguous US. Only hubs and metro or micro areas with ' +
          'volume are drawn, coloured by the measure in the filter bar. ' +
          'Clicking any shape selects the whole trade area it belongs to, ' +
          'because a distributor serves a radius rather than a city. Scroll to zoom, drag ' +
          'to pan.'
        }
      >
        <Suspense fallback={<div className="state">Loading map…</div>}>
          <CbsaMap
            areas={mapAreas}
            measureLabel={measureLabel}
            selected={selected}
            onSelect={setSelected}
          />
        </Suspense>
        {selected === null && (
          <p className="tam-hint">
            Click any area on the map, or a row in the table below, to open it.
          </p>
        )}
      </Card>

      {area !== undefined && (
        <Card
          title={text(area['trade_area_title']) ?? 'Trade area'}
          subtitle={`${text(area['region']) ?? NONE} · ${text(area['standing']) ?? NONE}`}
          actions={
            <button
              type="button"
              className="tam-clear"
              onClick={() => {
                setSelected(null)
              }}
            >
              Clear selection
            </button>
          }
        >
          <dl className="tam-facts">
            <Fact label="Restaurants" value={whole(num(area['food_service_estab']))} />
            <Fact label="Population" value={whole(num(area['population']))} />
            <Fact label="Median income" value={money(num(area['median_household_income']))} />
            <Fact label="Gallons TTM" value={whole(num(area['gallons_ttm']))} />
            <Fact label="Gal / 1k restaurants" value={whole(num(area['gal_per_1k_estab']))} />
            <Fact label="Region norm" value={whole(num(area['region_norm_per_1k']))} />
            <Fact label="% of region norm" value={pct(num(area['pct_of_region_norm']), 0)} />
            <Fact label="Headroom" value={`${whole(num(area['headroom_gal']))} gal`} />
            <Fact label="Distributors" value={String(count(area['distributors_present']))} />
            <Fact
              label="Metros"
              value={`${String(count(area['metros']))} (${String(
                count(area['metros_without_distributor']),
              )} without their own)`}
            />
            <Fact
              label="Furthest member"
              value={`${whole(num(area['furthest_member_miles']))} mi`}
            />
          </dl>

          <Section title="Metros and micro areas in this trade area">
            <table className="tam-table">
              <thead>
                <tr>
                  <th scope="col">Metro / micro area</th>
                  <th scope="col">Class</th>
                  <th scope="col">Role</th>
                  <th scope="col" className="cell-number">
                    Miles from hub
                  </th>
                  <th scope="col" className="cell-number">
                    Restaurants
                  </th>
                  <th scope="col" className="cell-number">
                    Population
                  </th>
                  <th scope="col" className="cell-number">
                    Distributors
                  </th>
                  <th scope="col" className="cell-number">
                    Gallons TTM
                  </th>
                </tr>
              </thead>
              <tbody>
                {areaMembers.map((row) => (
                  <tr key={String(count(row['cbsa_code']))}>
                    <td>{text(row['cbsa_title']) ?? NONE}</td>
                    <td>{text(row['cbsa_class']) ?? NONE}</td>
                    <td>
                      {text(row['is_hub']) === 'true' ? (
                        <span className="tam-hub">hub</span>
                      ) : (
                        (text(row['coverage']) ?? NONE)
                      )}
                    </td>
                    <td className="cell-number">{whole(num(row['miles_to_hub']))}</td>
                    <td className="cell-number">{whole(num(row['food_service_estab']))}</td>
                    <td className="cell-number">{whole(num(row['population']))}</td>
                    <td className="cell-number">{count(row['distributors_present'])}</td>
                    <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title={`Distributors serving it (${String(areaDistributors.length)})`}>
            {areaDistributors.length === 0 ? (
              <Empty label="No distributor ships into this area. That is the finding, not missing data." />
            ) : (
              <div className="tam-scroll">
                <table className="tam-table">
                  <thead>
                    <tr>
                      <th scope="col">Distributor</th>
                      <th scope="col">Ship-to</th>
                      <th scope="col">Metro</th>
                      <th scope="col">Channel</th>
                      <th scope="col" className="cell-number">
                        Lines
                      </th>
                      <th scope="col" className="cell-number">
                        Orders TTM
                      </th>
                      <th scope="col" className="cell-number">
                        Gallons TTM
                      </th>
                      <th scope="col">Last order</th>
                      <th scope="col" className="cell-number">
                        Days since
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaDistributors.map((row) => {
                      const shipTo = text(row['ship_to_number'])
                      const isOpen = shipTo !== null && shipTo === shownBranch
                      return (
                      <tr
                        key={shipTo ?? ''}
                        className={isOpen ? 'tam-row is-selected' : 'tam-row'}
                      >
                        <td>
                          <button
                            type="button"
                            className="tam-link"
                            aria-pressed={isOpen}
                            onClick={() => {
                              setBranch(isOpen ? null : shipTo)
                            }}
                          >
                            {text(row['distributor_group']) ?? NONE}
                          </button>
                        </td>
                        <td className="tam-dim">{shipTo ?? NONE}</td>
                        <td>{text(row['metro']) ?? NONE}</td>
                        <td>{text(row['distribution']) ?? NONE}</td>
                        <td className="cell-number">{count(row['lines_carried'])}</td>
                        <td className="cell-number">{count(row['orders_ttm'])}</td>
                        <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                        <td>{text(row['last_order_date']) ?? NONE}</td>
                        <td className="cell-number">{count(row['days_since_order'])}</td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="tam-note">
              Pick a distributor to see the lines that branch actually carries. The panel below
              follows the choice.
            </p>
          </Section>

          {areaConsolidators.length > 0 && (
            <Section title={`Excluded — shipped via a consolidator (${String(areaConsolidators.length)})`}>
              <p className="tam-note">
                Booked to a drop point inside this area but traded somewhere else, so they are
                not counted in any figure above. Edmonton is not Los Angeles, and until an
                address exists for these no area can own them.
              </p>
              <table className="tam-table">
                <thead>
                  <tr>
                    <th scope="col">Ship-to</th>
                    <th scope="col">Distributor</th>
                    <th scope="col">Drop point</th>
                    <th scope="col" className="cell-number">
                      Gallons TTM
                    </th>
                    <th scope="col" className="cell-number">
                      Lines
                    </th>
                    <th scope="col" className="cell-number">
                      Last order
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {areaConsolidators.map((row) => (
                    <tr key={text(row['ship_to_number']) ?? ''}>
                      <td>{text(row['ship_to_number']) ?? NONE}</td>
                      <td>{text(row['distributor_group']) ?? NONE}</td>
                      <td className="tam-dim">
                        {text(row['city']) ?? NONE}, {text(row['state']) ?? NONE}
                      </td>
                      <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                      <td className="cell-number">{count(row['lines_carried'])}</td>
                      <td className="cell-number">{text(row['last_order_date']) ?? NONE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          <Section
            title={
              shownBranch === null
                ? 'Product categories we sell there'
                : `Product categories — ${text(branchRow?.['distributor_group']) ?? 'this branch'}`
            }
          >
            <div className="tam-switch" role="group" aria-label="Product mix scope">
              <button
                type="button"
                className={shownBranch === null ? 'tam-switch-on' : 'tam-switch-off'}
                aria-pressed={shownBranch === null}
                onClick={() => {
                  setBranch(null)
                }}
              >
                Entire area
              </button>
              {shownBranch !== null && (
                <button
                  type="button"
                  className="tam-switch-on"
                  aria-pressed
                  onClick={() => {
                    setBranch(null)
                  }}
                >
                  {shownBranch}
                </button>
              )}
            </div>

            {shownBranch !== null ? (
              branchMix.length === 0 ? (
                <Empty label="This branch has shipped nothing in the trailing year." />
              ) : (
                <table className="tam-table">
                  <thead>
                    <tr>
                      <th scope="col">Product line</th>
                      <th scope="col" className="cell-number">
                        Gallons TTM
                      </th>
                      <th scope="col" className="cell-number">
                        Orders TTM
                      </th>
                      <th scope="col">Last order</th>
                      <th scope="col" className="cell-number">
                        Share at this branch
                      </th>
                      <th scope="col" className="cell-number">
                        Share across the area
                      </th>
                      <th scope="col" className="cell-number">
                        Gap
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchMix.map((row) => {
                      const gap = num(row['mix_gap'])
                      return (
                        <tr key={text(row['product_line']) ?? ''}>
                          <td>{text(row['product_line']) ?? NONE}</td>
                          <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                          <td className="cell-number">{count(row['orders_ttm'])}</td>
                          <td>{text(row['last_order_date']) ?? NONE}</td>
                          <td className="cell-number">{pct(num(row['pct_of_branch_mix']))}</td>
                          <td className="cell-number">{pct(num(row['pct_of_area_mix']))}</td>
                          <td
                            className={
                              gap !== null && gap < 0 ? 'cell-number tam-behind' : 'cell-number'
                            }
                          >
                            {signedPct(gap)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )
            ) : areaProducts.length === 0 ? (
              <Empty label="Nothing sold in this area." />
            ) : (
              <table className="tam-table">
                <thead>
                  <tr>
                    <th scope="col">Product line</th>
                    <th scope="col" className="cell-number">
                      Gallons TTM
                    </th>
                    <th scope="col" className="cell-number">
                      Ship-tos
                    </th>
                    <th scope="col" className="cell-number">
                      Distributors
                    </th>
                    <th scope="col" className="cell-number">
                      Share here
                    </th>
                    <th scope="col" className="cell-number">
                      Share across region
                    </th>
                    <th scope="col" className="cell-number">
                      Gap
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {areaProducts.map((row) => {
                    const gap = num(row['mix_gap'])
                    return (
                      <tr key={text(row['product_line']) ?? ''}>
                        <td>{text(row['product_line']) ?? NONE}</td>
                        <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                        <td className="cell-number">{count(row['ship_tos'])}</td>
                        <td className="cell-number">{count(row['distributors_present'])}</td>
                        <td className="cell-number">{pct(num(row['pct_of_area_mix']))}</td>
                        <td className="cell-number">{pct(num(row['region_pct_of_mix']))}</td>
                        <td
                          className={
                            gap !== null && gap < 0 ? 'cell-number tam-behind' : 'cell-number'
                          }
                        >
                          {signedPct(gap)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Lines the region sells that this area does not">
            {areaAbsent.length === 0 ? (
              <Empty label="This area carries every line its region sells." />
            ) : (
              <table className="tam-table">
                <thead>
                  <tr>
                    <th scope="col">Product line</th>
                    <th scope="col" className="cell-number">
                      Share across region
                    </th>
                    <th scope="col" className="cell-number">
                      Implied gallons at that mix
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {areaAbsent.map((row) => (
                    <tr key={text(row['product_line']) ?? ''}>
                      <td>{text(row['product_line']) ?? NONE}</td>
                      <td className="cell-number">{pct(num(row['region_pct_of_mix']))}</td>
                      <td className="cell-number">{whole(num(row['implied_gallons']))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {areaFindings.length > 0 && (
            <Section title={`Open agent findings (${String(areaFindings.length)})`}>
              <table className="tam-table">
                <thead>
                  <tr>
                    <th scope="col">Agent</th>
                    <th scope="col">Severity</th>
                    <th scope="col">Finding</th>
                    <th scope="col" className="cell-number">
                      Gal at risk
                    </th>
                    <th scope="col" className="cell-number">
                      $ at risk
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {areaFindings.map((row, index) => (
                    <tr key={`${text(row['title']) ?? ''}-${String(index)}`}>
                      <td>{text(row['agent_code']) ?? NONE}</td>
                      <td>{text(row['severity']) ?? NONE}</td>
                      <td className="tam-wrap">{text(row['title']) ?? NONE}</td>
                      <td className="cell-number">{whole(num(row['gallons_at_risk']))}</td>
                      <td className="cell-number">{money(num(row['dollars_at_risk']))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </Card>
      )}

      <Card
        title="By region"
        subtitle={
          'Every area is benchmarked against its own region, never nationally. Penetration ' +
          'is largely a map of freight distance from the two plants, so a national ' +
          'benchmark would just rank areas by how far they are from Arizona and Florida.'
        }
      >
        <table className="tam-table">
          <thead>
            <tr>
              <th scope="col">Region</th>
              <th scope="col" className="cell-number">
                Areas
              </th>
              <th scope="col" className="cell-number">
                Restaurants
              </th>
              <th scope="col" className="cell-number">
                Population
              </th>
              <th scope="col" className="cell-number">
                Gallons TTM
              </th>
              <th scope="col" className="cell-number">
                Norm gal / 1k
              </th>
              <th scope="col" className="cell-number">
                Headroom gal
              </th>
            </tr>
          </thead>
          <tbody>
            {regionTotals.map((row) => (
              <tr key={text(row['region']) ?? ''}>
                <td>{text(row['region']) ?? NONE}</td>
                <td className="cell-number">{count(row['areas'])}</td>
                <td className="cell-number">{whole(num(row['food_service_estab']))}</td>
                <td className="cell-number">{whole(num(row['population']))}</td>
                <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                <td className="cell-number">{whole(num(row['region_norm_per_1k']))}</td>
                <td className="cell-number">{whole(num(row['headroom_gal']))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        title="Every trade area"
        subtitle="Ranked by headroom to the region median. Click a row to open it on the map."
      >
        <div className="tam-scroll tam-tall">
          <table className="tam-table">
            <thead>
              <tr>
                <th scope="col">Trade area</th>
                <th scope="col">Region</th>
                <th scope="col">Standing</th>
                <th scope="col" className="cell-number">
                  Metros
                </th>
                <th scope="col" className="cell-number">
                  Restaurants
                </th>
                <th scope="col" className="cell-number">
                  Distributors
                </th>
                <th scope="col" className="cell-number">
                  Gallons TTM
                </th>
                <th scope="col" className="cell-number">
                  Gal / 1k
                </th>
                <th scope="col" className="cell-number">
                  % of norm
                </th>
                <th scope="col" className="cell-number">
                  Headroom
                </th>
              </tr>
            </thead>
            <tbody>
              {areas.map((row) => {
                const code = count(row['trade_area_code'])
                return (
                  <tr
                    key={String(code)}
                    className={code === selected ? 'tam-row is-selected' : 'tam-row'}
                  >
                    <td>
                      <button
                        type="button"
                        className="tam-link"
                        onClick={() => {
                          setSelected(code)
                        }}
                      >
                        {text(row['trade_area_title']) ?? NONE}
                      </button>
                    </td>
                    <td>{text(row['region']) ?? NONE}</td>
                    <td>{text(row['standing']) ?? NONE}</td>
                    <td className="cell-number">{count(row['metros'])}</td>
                    <td className="cell-number">{whole(num(row['food_service_estab']))}</td>
                    <td className="cell-number">{count(row['distributors_present'])}</td>
                    <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                    <td className="cell-number">{whole(num(row['gal_per_1k_estab']))}</td>
                    <td className="cell-number">{pct(num(row['pct_of_region_norm']), 0)}</td>
                    <td className="cell-number">{whole(num(row['headroom_gal']))}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="As of — what each figure was measured from" subtitle="">
        <table className="tam-table">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">As of</th>
              <th scope="col">What it means</th>
            </tr>
          </thead>
          <tbody>
            {asOf.map((row) => (
              <tr key={text(row['source']) ?? ''}>
                <td>{text(row['source']) ?? NONE}</td>
                <td>{text(row['as_of']) ?? NONE}</td>
                <td className="tam-wrap tam-dim">{text(row['note']) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <article className="tam-kpi">
      <p className="tam-kpi-label">{label}</p>
      <p className="tam-kpi-value cell-number">{value}</p>
    </article>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="tam-section">
      <h3 className="tam-section-title">{title}</h3>
      {children}
    </section>
  )
}
